import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Per-entity financial stats (supplier / client / subscription) — mirrors
// backend/src/services/entityFinancialStats.ts. Consumed by the shared
// FinancialStatsSection on the detail pages.

export interface EntityStatsTransaction {
  id: string;
  date: string; // ISO
  description: string | null;
  amount: number;
  currency: string;
  direction: 'IN' | 'OUT';
  source: 'bank' | 'invoice' | 'smartbill';
  paymentMethod: string | null;
  status: string | null;
  invoiceNumber: string | null;
}

export interface EntityFinancialStats {
  // Dominant currency (most frequent across transactions, default RON).
  currency: string;
  totals: {
    totalAmount: number;
    transactionCount: number;
    monthlyAverage: number;
    lastPayment: { date: string; amount: number; currency: string } | null;
  };
  // 'YYYY-MM', last 12 months, zero-filled, dominant currency only.
  monthly: { month: string; amount: number }[];
  // Desc by date, max 100, each row in its own currency.
  transactions: EntityStatsTransaction[];
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchStats(path: string): Promise<EntityFinancialStats> {
  const res = await apiFetch<Envelope<EntityFinancialStats>>(path, { headers: authHeaders() });
  return res.data;
}

export function getSubscriptionStats(id: string): Promise<EntityFinancialStats> {
  return fetchStats(`/api/subscriptions/${id}/stats`);
}

export function getSupplierStats(id: string): Promise<EntityFinancialStats> {
  return fetchStats(`/api/suppliers/${id}/stats`);
}

export function getClientStats(id: string): Promise<EntityFinancialStats> {
  return fetchStats(`/api/clients/${id}/stats`);
}
